import { ChangeDetectorRef, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NgxTurnstileModule } from 'ngx-turnstile';
import { AuthService } from '../auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { interval, map, Subscription, takeWhile } from 'rxjs';
import { ChangePasswordPublicComponent } from '../../components/change-password-public/change-password-public.component';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    NgxTurnstileModule,
    ReactiveFormsModule,
    ChangePasswordPublicComponent
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {

  private cooldownSubscription: Subscription | null = null;

  private snackBar = inject(MatSnackBar);
  forgotPasswordForm: FormGroup;
  disableSubmitButton: boolean = true;
  showOtpField: boolean = false;

  isCooldownActive = false;
  cooldownTime: number = 0;

  userName!: string;
  mobileNumber!: string;

  showChangePassword: boolean = false;
  resendOTPButton: boolean = false;
  captchaResponse: string = '';

  maskedEmail: string = '';

  constructor(
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private router: Router,
  ) {
    this.forgotPasswordForm = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(4)]],
      mobileNumber: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(10), Validators.pattern('^[- +()0-9]+$')]],
      otp: ['', [Validators.minLength(6), Validators.maxLength(6), Validators.pattern("[0-9]+")]]
    });
  }

  ngOnInit(): void {
    // Add event listener for browser/tab close
    window.addEventListener('beforeunload', this.clearLocalStorage);
  }

  ngOnDestroy(): void {
    // Remove event listener on component destroy
    if (this.cooldownSubscription) {
      this.cooldownSubscription.unsubscribe();
    }
    window.removeEventListener('beforeunload', this.clearLocalStorage);
  }

  clearLocalStorage(): void {
    // Clear localStorage
    localStorage.clear();
  }

  @HostListener('window:beforeunload', ['$event'])
  handleUnload(event: any) {
    // Retrieve the access token from localStorage
    const token = localStorage.getItem('accessToken');
    const url = `${environment.apiUrl}/auth/v1/public/logout?token=${token}`;
    // Send a beacon request
    navigator.sendBeacon(url);  
    // Optional: if you need to clear localStorage
    localStorage.clear();
  }

  sendCaptchaResponse(captchaRes: any) {
    console.log(`Resolved captcha with response: ${captchaRes}`);
    if (captchaRes) {
      this.disableSubmitButton = false;
      this.captchaResponse = captchaRes;
    }
  }

  submit() {
    const { userName, mobileNumber, otp } = this.forgotPasswordForm.value;
    if (otp === null || otp.trim() === '') {
      if (this.forgotPasswordForm.valid) {
        this.authService.verifyUserAndRequestOtp({
          userName: userName ? userName.trim() : this.userName,
          mobileNumber: mobileNumber ? mobileNumber.trim() : this.mobileNumber,
          email: null,
          otp: null,
          source: null,
          captchaResponse: this.captchaResponse
        }).subscribe({
          next: async (response) => {
            // console.log('POST response:', response);
            if (response.success) {
              this.showOtpField = response.success;
              this.resendOTPButton = true;
              this.forgotPasswordForm.controls['userName'].disable();
              this.forgotPasswordForm.controls['mobileNumber'].disable();
              this.userName = userName;
              this.mobileNumber = mobileNumber;
              this.maskedEmail = response.maskedEmail;
              this.cooldownTime = response.cooldownTime;
              this.startCooldownTimer(this.cooldownTime);
              this.cdr.detectChanges();
            } else {
              this.cooldownTime = response.cooldownTime;
              this.resendOTPButton = true;
              this.startCooldownTimer(this.cooldownTime);
              this.maskedEmail = response.maskedEmail;
              this.openSnackBar(response.message, 'Okay')
            }
          },
          error: (error) => {
            console.error('POST error:', error);
            this.startCooldownTimer(0);
            this.openSnackBar('Username or Mobile Number not valid', 'Okay')
          },
        });
      } else {
        this.openSnackBar('Please enter valid username and mobile number', 'Okay');
      }
    } else {
      this.authService.validateRequestedOtp({
        userName: this.userName,
        mobileNumber: this.mobileNumber,
        otp: otp.trim(),
        email: null,
        source: null,
        captchaResponse: null
      }).subscribe({
        next: async (response) => {
          // console.log('POST response:', response);
          if (response.loginSuccess) {
            this.showChangePassword = response.loginSuccess;
            localStorage.setItem('accessToken', response.token);
            this.cdr.detectChanges();
          } else {
            this.openSnackBar('Invalid or Expired OTP', 'Okay')
          }
        },
        error: (error) => {
          console.error('POST error:', error);
          this.openSnackBar('Username or Mobile Number not valid', 'Okay');
        },
      });
    }
  }

  resendOtp() {
    // console.log(this.userName, this.mobileNumber);
    this.authService.verifyUserAndRequestOtp({
      userName: this.userName,
      mobileNumber: this.mobileNumber,
      email: null,
      otp: null,
      source: null,
      captchaResponse: this.captchaResponse
    }).subscribe({
      next: async (response) => {
        if (response.success) {
          this.showOtpField = response.success;
          this.resendOTPButton = true;
          this.forgotPasswordForm.controls['userName'].disable();
          this.forgotPasswordForm.controls['mobileNumber'].disable();
          this.cooldownTime = response.cooldownTime;
          this.maskedEmail = response.maskedEmail;
          this.startCooldownTimer(this.cooldownTime);
          this.cdr.detectChanges();
        } else {
          this.cooldownTime = response.cooldownTime;
          this.resendOTPButton = true;
          this.startCooldownTimer(this.cooldownTime);
          this.maskedEmail = response.maskedEmail;
          this.openSnackBar(response.message, 'Okay')
        }
      },
      error: (error) => {
        console.error('POST error:', error);
        this.startCooldownTimer(0);
        this.openSnackBar('Username or Mobile Number not valid', 'Okay')
      },
    });
  }

  startCooldownTimer(seconds: number): void {
    // Unsubscribe from any existing cooldown timer
    if (this.cooldownSubscription) {
      this.cooldownSubscription.unsubscribe();
    }

    this.isCooldownActive = true;
    this.cooldownSubscription = interval(1000).pipe(
      takeWhile(() => seconds > 0),
      map(() => seconds--)
    ).subscribe({
      next: remainingTime => this.cooldownTime = remainingTime,
      complete: () => {
        this.isCooldownActive = false;
        this.cooldownSubscription = null; // Reset subscription after completion
      }
    });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 3 * 1000 }); // 3 seconds
  }

  backToLogin() {
    this.router.navigate(['/app/login'], { replaceUrl: true });
  }
}
